import { Button } from '@/components/ui/button'
import {
  Alert,
  Fade,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { useState } from 'react'
import { toast } from 'sonner'
import { EnumAccessLevel } from '../../../@types'
import { Modal } from '../../../components'
import { useInputText } from '../../../hooks'
import services from '../../../services'
import { ModalRegisterProps } from './@types'
import { useData } from './use-data'

export default function ModalRegister({ open, onClose }: ModalRegisterProps) {
  const { ROLES, refetch } = useData()
  const initialValues = { email: '', name: '', surname: '' }
  const input = useInputText(initialValues)

  const [level, setLevel] = useState<number>(1)
  const [accessLevel, setAccessLevel] = useState<EnumAccessLevel>('STUDENT')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const onAccessLevelChange = (event: SelectChangeEvent) =>
    setAccessLevel(event.target.value.toUpperCase() as EnumAccessLevel)

  const onLevelChange = (event: SelectChangeEvent) =>
    setLevel(Number(event.target.value))

  const closeModal = () => {
    refetch()
    input.clear()
    setLevel(1)
    setError(null)
    onClose()
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      if (error) setError(null)
      setLoading(true)

      const { email, name, surname } = input
      const password = `Win_${name.toLowerCase()}_${new Date().getFullYear()}`

      await services.user.create({
        accessLevel,
        email,
        name,
        password,
        surname,
      })

      toast.message('Utilizador criado com sucesso.')

      closeModal()
    } catch (error) {
      setError((error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={closeModal}
      head={<h2 className="text-xl font-bold">Registar Novo Aluno</h2>}
    >
      <Fade in={open}>
        <div className="text-xl grid gap-12">
          {loading && <Alert severity="warning">A enviar dados...</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <form onSubmit={handleSubmit} className="grid gap-8">
            <div className="grid gap-4">
              <TextField
                autoFocus
                required
                label="Nome próprio"
                name="name"
                value={input.name}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.name}
                helperText={input.errors.name}
                disabled={loading}
              />

              <TextField
                required
                label="Apelido"
                name="surname"
                value={input.surname}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.surname}
                helperText={input.errors.surname}
                disabled={loading}
              />
            </div>

            <div className="grid gap-4">
              <TextField
                required
                label="E-mail"
                type="email"
                name="email"
                value={input.email}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.email}
                helperText={input.errors.email}
                disabled={loading}
              />

              <Select
                value={accessLevel}
                onChange={onAccessLevelChange}
                disabled={loading}
              >
                {ROLES.map((role) => (
                  <MenuItem
                    disabled={role !== 'STUDENT' || loading}
                    key={role}
                    value={role}
                  >
                    {role}
                  </MenuItem>
                ))}
              </Select>

              <Select
                className="!hidden"
                value={String(level)}
                onChange={onLevelChange}
                disabled={loading}
              >
                {[1, 2, 3, 4, 5].map((index) => (
                  <MenuItem disabled={loading} key={index} value={index}>
                    {String(index)}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="w-max max-w-full ml-auto mt-4 gap-4 flex">
              <Button
                disabled={loading}
                variant="outline"
                type="reset"
                onClick={closeModal}
              >
                Cancelar
              </Button>
              <Button disabled={loading} type="submit">
                Submeter
              </Button>
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  )
}
